import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash2, Save, ArrowLeft, Download, Upload, Moon, Mail } from "lucide-react";
import api, { formatApiError, cachedGet, apiCache } from "@/lib/api";
import { savePdf } from "@/lib/savePdf";
import { shareOrMailtoPdf } from "@/lib/sharePdf";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import FieldHelp from "@/components/FieldHelp";
import CsvImportDialog from "@/components/CsvImportDialog";
import EmailRecipientDialog from "@/components/EmailRecipientDialog";
import { useAuth } from "@/contexts/AuthContext";

const TASKS = ["personal care", "social support", "house-hold duties", "cleaning", "yard maintenance", "travel"];
const RATE_TYPES = ["weekday_day", "weekday_evening", "weekday_night", "sleepover", "saturday", "sunday", "public_holiday"];

export default function TimesheetsList() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState({ status: "" });
  const [importOpen, setImportOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const load = async () => {
    try {
      // Stale-while-revalidate: render cached rows instantly, then update on fresh fetch.
      await cachedGet("/timesheets", filter, (data) => setRows(data));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [filter.status]);

  const onDelete = async (id) => {
    if (!confirm("Delete this timesheet?")) return;
    try {
      const r = await api.delete(`/timesheets/${id}`);
      if (r.data?.pending_approval) toast("Deletion sent to client for approval");
      else {
        toast.success("Timesheet deleted");
        // Optimistic local update — no second round-trip needed.
        setRows((prev) => prev.filter((row) => row.id !== id));
      }
      apiCache.invalidate("/timesheets");
      apiCache.invalidate("/notifications");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadShiftNotes = async () => {
    try {
      const res = await api.get("/shift-notes/pdf", { responseType: "blob" });
      await savePdf(res.data, "shift_notes.pdf");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5 max-w-6xl" data-testid="timesheets-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-4xl font-display font-black tracking-tight">Timesheets</h1>
          <p className="text-muted-foreground">Log shifts and track approvals.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={downloadShiftNotes} variant="outline" className="rounded-full min-h-[48px] font-bold gap-2" data-testid="shift-notes-pdf-btn">
            <Download className="w-4 h-4" /> Shift notes PDF
          </Button>
          <Button onClick={() => setEmailOpen(true)} variant="outline" className="rounded-full min-h-[48px] font-bold gap-2" data-testid="timesheets-email-btn">
            <Mail className="w-4 h-4" /> Email
          </Button>
          {user?.role === "client" && (
            <Button onClick={() => setImportOpen(true)} variant="outline" className="rounded-full min-h-[48px] font-bold gap-2" data-testid="import-csv-btn">
              <Upload className="w-4 h-4" /> Import CSV
            </Button>
          )}
          <Link to="/timesheets/new"><Button className="rounded-full min-h-[48px] font-bold gap-2" data-testid="new-timesheet-btn"
            style={{ background: "hsl(var(--client))" }}><Plus className="w-4 h-4" /> New timesheet</Button></Link>
        </div>
      </header>

      <CsvImportDialog
        open={importOpen} onOpenChange={setImportOpen}
        title="Import timesheets from CSV"
        description="Bulk-add shifts in one go. One row = one timesheet."
        templateUrl="/timesheets/import/template"
        importUrl="/timesheets/import"
        templateFilename="timesheet_template.csv"
        testIdPrefix="ts-import"
        onDone={() => load()}
      />

      <EmailRecipientDialog
        open={emailOpen} onOpenChange={setEmailOpen}
        title="Email shift notes"
        description="Pick recipients from your address book or add new ones. The latest shift-notes PDF will be attached."
        onSend={async (recipients) => {
          const res = await api.get("/shift-notes/pdf", { responseType: "blob" });
          const result = await shareOrMailtoPdf({
            blob: res.data,
            filename: "shift_notes.pdf",
            recipients,
            subject: "Shift notes",
            body: `Hi,\n\nPlease find the latest shift-notes report attached.\n\nKind regards,\n${user?.full_name || ""}`.trim(),
          });
          if (result === "shared") toast.success("Opened in your email app");
          else if (result === "mailto") toast.success(`PDF saved as “shift_notes.pdf” in Downloads. Attach it in the email composer that just opened.`, { duration: 12000 });
        }}
      />

      <div className="flex gap-2 flex-wrap">
        {["", "pending", "approved", "rejected"].map((s) => (
          <Button key={s || "all"} size="sm" variant={filter.status === s ? "default" : "outline"}
            onClick={() => setFilter({ status: s })}
            data-testid={`filter-${s || "all"}`}
            className="rounded-full font-bold capitalize">{s || "all"}</Button>
        ))}
      </div>

      <Card className="rounded-3xl border-2 overflow-hidden" data-testid="timesheets-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wider font-bold">
              <tr><th className="text-left p-3">Date</th><th className="text-left p-3">Worker</th><th className="text-left p-3">Hours</th><th className="text-left p-3">Rate</th><th className="text-left p-3">Pay</th><th className="text-left p-3">Status</th><th className="p-3"></th></tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id} className="border-t" data-testid={`ts-row-${t.id}`}>
                  <td className="p-3 font-bold">{t.date}<div className="text-xs text-muted-foreground">{t.start_time}–{t.end_time}</div></td>
                  <td className="p-3">{t.worker_name}</td>
                  <td className="p-3 font-mono">{t.shift_duration_hours}h</td>
                  <td className="p-3 capitalize">{t.pay_rate_type.replace(/_/g," ")}</td>
                  <td className="p-3 font-mono font-bold">${t.total_pay.toFixed(2)}</td>
                  <td className="p-3"><Badge variant={t.status === "approved" ? "default" : t.status === "rejected" ? "destructive" : "secondary"} className="rounded-full font-bold uppercase tracking-wider text-[10px]">{t.status}</Badge></td>
                  <td className="p-3 flex gap-1">
                    <Link to={`/timesheets/${t.id}`}><Button size="sm" variant="ghost" className="rounded-full" data-testid={`edit-ts-${t.id}`}>Edit</Button></Link>
                    <Button size="sm" variant="ghost" className="rounded-full text-destructive" onClick={() => onDelete(t.id)} data-testid={`delete-ts-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan="7" className="p-8 text-center text-muted-foreground">No timesheets yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

export function TimesheetForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const [workers, setWorkers] = useState([]);
  const [codes, setCodes] = useState([]);
  const [rateTypes, setRateTypes] = useState(RATE_TYPES);
  const [form, setForm] = useState({
    worker_id: "", date: new Date().toISOString().slice(0, 10),
    start_time: "09:00", end_time: "12:00", pay_rate_type: "weekday_day",
    tasks: [{ task: "personal care", ndis_code: "", hours: 1 }],
    shift_notes: "",
    wakeup_minutes: 0,
  });
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target?.value ?? e }));

  useEffect(() => {
    (async () => {
      const w = await api.get("/workers"); setWorkers(w.data);
      const c = await api.get("/ndis-codes"); setCodes(c.data.codes); setRateTypes(c.data.rate_types);
      if (id) {
        const t = await api.get("/timesheets");
        const found = t.data.find((x) => x.id === id);
        if (found) setForm(found);
      } else if (w.data[0]) setForm((f) => ({ ...f, worker_id: w.data[0].id }));
    })();
  }, [id]);

  const duration = useMemo(() => {
    const [sh, sm] = form.start_time.split(":").map(Number);
    const [eh, em] = form.end_time.split(":").map(Number);
    let m = (eh*60+em) - (sh*60+sm);
    if (m < 0) m += 24*60;
    return Math.round((m/60)*100)/100;
  }, [form.start_time, form.end_time]);

  const worker = workers.find((w) => w.id === form.worker_id);
  const rate = worker?.pay_rates?.[form.pay_rate_type] || 0;
  const isSleepover = form.pay_rate_type === "sleepover";
  const activeNightRate = worker?.pay_rates?.weekday_night || 0;
  const wakeupMins = Math.max(parseInt(form.wakeup_minutes, 10) || 0, 0);
  const basePay = isSleepover ? rate : duration * rate;
  const wakeupPay = isSleepover ? (wakeupMins / 60) * activeNightRate : 0;
  const totalPay = (basePay + wakeupPay).toFixed(2);

  const filteredCodes = (task) => codes.filter((c) => c.task === task && c.rate_type === form.pay_rate_type);

  // Auto-fill / auto-correct NDIS code on every task whenever pay_rate_type or task changes,
  // so users can't accidentally bill a Saturday shift under a Sunday code. We only override
  // when the currently-selected code doesn't match the new (task, rate_type) pair.
  useEffect(() => {
    setForm((f) => {
      let changed = false;
      const tasks = f.tasks.map((t) => {
        const matches = codes.filter((c) => c.task === t.task && c.rate_type === f.pay_rate_type);
        if (matches.length === 0) return t;
        if (matches.some((m) => m.code === t.ndis_code)) return t;  // already correct
        changed = true;
        return { ...t, ndis_code: matches[0].code };
      });
      return changed ? { ...f, tasks } : f;
    });
  }, [form.pay_rate_type, form.tasks.map((t) => t.task).join("|"), codes]);  // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    try {
      if (id) await api.put(`/timesheets/${id}`, form);
      else await api.post("/timesheets", form);
      toast.success("Timesheet saved");
      apiCache.invalidate("/timesheets");
      apiCache.invalidate("/notifications");
      nav("/timesheets");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5 max-w-3xl" data-testid="timesheet-form-page">
      <Link to="/timesheets" className="text-sm text-muted-foreground inline-flex items-center gap-1" data-testid="back-link"><ArrowLeft className="w-4 h-4" /> Back</Link>
      <h1 className="text-4xl font-display font-black tracking-tight">{id ? "Edit timesheet" : "New timesheet"}</h1>

      <Card className="rounded-3xl p-6 border-2 space-y-4" data-testid="timesheet-card">
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase font-bold tracking-wider">Worker</Label>
            <Select value={form.worker_id} onValueChange={upd("worker_id")}>
              <SelectTrigger className="min-h-[48px] rounded-xl" data-testid="select-worker"><SelectValue placeholder="Select worker" /></SelectTrigger>
              <SelectContent>{workers.map((w) => <SelectItem key={w.id} value={w.id}>{w.full_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase font-bold tracking-wider flex items-center">
              Pay rate type
              <FieldHelp text="Picks the right hourly rate. Weekday day = 6am-8pm Mon-Fri. Evening = 8pm-midnight. Night = midnight-6am. Saturday, Sunday and public holidays each have their own rate." learnMoreId="timesheets" />
            </Label>
            <Select value={form.pay_rate_type} onValueChange={upd("pay_rate_type")}>
              <SelectTrigger className="min-h-[48px] rounded-xl" data-testid="select-rate-type"><SelectValue /></SelectTrigger>
              <SelectContent>{rateTypes.map((r) => <SelectItem key={r} value={r}>{r.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Field id="date" label="Date" type="date" v={form.date} oc={upd("date")} />
          <div className="grid grid-cols-2 gap-3">
            <Field id="start_time" label="Start" type="time" v={form.start_time} oc={upd("start_time")} />
            <Field id="end_time" label="Finish" type="time" v={form.end_time} oc={upd("end_time")} />
          </div>
        </div>

        {isSleepover && (
          <Card className="bg-indigo-500/5 border-2 border-indigo-500/30 rounded-2xl p-4 space-y-2" data-testid="sleepover-card">
            <div className="flex items-center gap-2">
              <Moon className="w-4 h-4 text-indigo-500" />
              <Label className="text-sm font-bold uppercase tracking-wider">Sleepover details</Label>
            </div>
            <p className="text-xs text-muted-foreground">
              Sleepovers are billed as a flat <b>${rate.toFixed(2)}</b> per shift (NDIS max 8 hours).
              If the participant woke you up, log the active-assistance time below — it's billed on top
              at the worker's <b>weekday-night rate (${activeNightRate.toFixed(2)}/h)</b>.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-bold tracking-wider flex items-center">
                  Wake-up minutes
                  <FieldHelp text="Total minutes you were actively assisting during the night (answered the bell, helped to the bathroom, etc.). Sleepover rate covers the rest."
                             learnMoreId="timesheets" />
                </Label>
                <Input
                  type="number" min="0" step="5"
                  value={form.wakeup_minutes ?? 0}
                  onChange={(e) => setForm((f) => ({ ...f, wakeup_minutes: parseInt(e.target.value, 10) || 0 }))}
                  data-testid="field-wakeup-minutes"
                  className="rounded-xl min-h-[48px]"
                  placeholder="0"
                />
              </div>
              <div className="text-xs text-muted-foreground pt-6 sm:text-right" data-testid="wakeup-breakdown">
                {wakeupMins > 0 ? (
                  <>+ <b className="text-foreground">${wakeupPay.toFixed(2)}</b> for {wakeupMins} min ({(wakeupMins/60).toFixed(2)}h × ${activeNightRate.toFixed(2)}/h)</>
                ) : (
                  <span>No wake-ups · base sleepover only</span>
                )}
              </div>
            </div>
          </Card>
        )}

        <Card className="bg-muted/40 border-2 p-4 rounded-2xl flex flex-wrap gap-4" data-testid="ts-summary">
          <Stat label="Duration" value={`${duration}h`} />
          {isSleepover ? (
            <>
              <Stat label="Base (flat)" value={`$${basePay.toFixed(2)}`} />
              <Stat label="Wake-up pay" value={`$${wakeupPay.toFixed(2)}`} />
              <Stat label="Total" value={`$${totalPay}`} accent />
            </>
          ) : (
            <>
              <Stat label="Rate" value={`$${rate.toFixed(2)}/h`} />
              <Stat label="Estimated pay" value={`$${totalPay}`} accent />
            </>
          )}
        </Card>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs uppercase font-bold tracking-wider flex items-center">
              Tasks completed
              <FieldHelp text="Each task you ticked off this shift. The NDIS code dropdown narrows automatically based on the task and pay rate type, so the right billing line is always picked." learnMoreId="timesheets" />
            </Label>
            <Button size="sm" variant="outline" className="rounded-full font-bold" onClick={() => setForm((f) => ({ ...f, tasks: [...f.tasks, { task: "personal care", ndis_code: "", hours: 1 }] }))} data-testid="add-task-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add task
            </Button>
          </div>
          {form.tasks.map((t, idx) => (
            <div key={idx} className="grid grid-cols-1 sm:grid-cols-[1fr,1fr,120px,40px] gap-2 p-3 rounded-xl border" data-testid={`task-row-${idx}`}>
              <Select value={t.task} onValueChange={(v) => setForm((f) => { const tasks = [...f.tasks]; tasks[idx] = { ...tasks[idx], task: v, ndis_code: "" }; return { ...f, tasks }; })}>
                <SelectTrigger className="rounded-xl" data-testid={`task-select-${idx}`}><SelectValue /></SelectTrigger>
                <SelectContent>{TASKS.map((tk) => <SelectItem key={tk} value={tk}>{tk}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={t.ndis_code} onValueChange={(v) => setForm((f) => { const tasks = [...f.tasks]; tasks[idx] = { ...tasks[idx], ndis_code: v }; return { ...f, tasks }; })}>
                <SelectTrigger className="rounded-xl" data-testid={`ndis-select-${idx}`}><SelectValue placeholder="NDIS code" /></SelectTrigger>
                <SelectContent>
                  {filteredCodes(t.task).length === 0 ? (
                    <SelectItem value="none" disabled>No code for this rate</SelectItem>
                  ) : filteredCodes(t.task).map((c) => (
                    <SelectItem key={c.code} value={c.code}>{c.code} — {c.description.slice(0, 40)}…</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input type="number" step="0.25" placeholder="hrs" value={t.hours}
                onChange={(e) => setForm((f) => { const tasks = [...f.tasks]; tasks[idx] = { ...tasks[idx], hours: parseFloat(e.target.value) || 0 }; return { ...f, tasks }; })}
                className="rounded-xl" data-testid={`task-hours-${idx}`} />
              <Button variant="ghost" size="icon" className="text-destructive" onClick={() => setForm((f) => ({ ...f, tasks: f.tasks.filter((_, i) => i !== idx) }))} data-testid={`task-delete-${idx}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs uppercase font-bold tracking-wider">Shift notes</Label>
          <Textarea rows={4} value={form.shift_notes} onChange={upd("shift_notes")} className="rounded-xl" data-testid="shift-notes-input" />
        </div>

        <div className="flex gap-2 justify-end">
          <Link to="/timesheets"><Button variant="outline" className="rounded-full font-bold">Cancel</Button></Link>
          <Button onClick={save} className="rounded-full font-bold gap-2" style={{ background: "hsl(var(--client))" }} data-testid="save-timesheet-btn">
            <Save className="w-4 h-4" /> Save
          </Button>
        </div>
      </Card>
    </div>
  );
}

function Field({ id, label, v, oc, type = "text" }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs uppercase font-bold tracking-wider">{label}</Label>
      <Input id={id} type={type} value={v || ""} onChange={oc} className="min-h-[48px] rounded-xl" data-testid={`field-${id}`} />
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{label}</div>
      <div className={`font-display font-black text-2xl ${accent ? "text-client" : ""}`} data-testid={`summary-${label.toLowerCase().replace(/[^a-z]+/g,'-')}`}>{value}</div>
    </div>
  );
}
